import { z } from 'zod';
import { v1ScopeSchema } from './scopes';
/**
 * `GET /api/v1/organizations/:organizationId/health` (KIR-3747).
 *
 * The canary for the public surface: it proves a customer's key authenticated,
 * resolved to the right tenant, and was attributed to the right creator —
 * without exposing any org data. It is what a customer curls first, and what a
 * connector's setup screen calls to validate credentials.
 *
 * `userId` is the local id of whoever minted the key, which is also the actor
 * every write made with it will be attributed to (design D2). `apiKeyId` is the
 * Descope access-key id, so a customer holding several keys can tell which one
 * a given credential is.
 */
export const v1HealthResponseSchema = z
    .object({
    ok: z
        .literal(true)
        .describe('Always `true`. A failing credential answers 401 or 403 instead.'),
    organizationId: z
        .string()
        .describe('The organization this key is scoped to. The only one it can read.'),
    userId: z
        .string()
        .describe('Kirin user ID of whoever created the key. Every write made with it is attributed to this person.'),
    apiKeyId: z
        .string()
        .describe('Id of the key that authenticated this request. Use it to tell which of several keys a credential is.'),
    /**
     * The key's scopes (KIR-4377).
     *
     * Belongs on this endpoint more than anywhere else: health exists to answer
     * "what does this credential do?", and after scopes the honest answer is no
     * longer just which organization and which person — it is also which
     * resources. A connector's setup screen can show it, and a 403 elsewhere can
     * be diagnosed without guessing.
     *
     * A key minted before scopes existed is unrestricted, and reports the full
     * list rather than an empty one or a null.
     */
    scopes: z
        .array(v1ScopeSchema)
        .describe('Access scopes this key holds. A `:write` scope also confers its `:read`. Keys created before scopes existed are unrestricted and report every scope.'),
})
    .meta({
    id: 'V1Health',
    title: 'Health',
    description: 'What the presented credential resolved to. Reads no organization data.',
    examples: [
        {
            ok: true,
            organizationId: 'T35Ne5HxIxGiPAMt69Jeg91Qpw3J',
            userId: '3fb9c1d4-7e62-4a08-9c53-2d81f0a64e7b',
            apiKeyId: 'K2vQ7RtLbN4sYwZ9hJ3dFcTpXmA8',
            scopes: ['alerts:read', 'audit-logs:read'],
        },
    ],
});
//# sourceMappingURL=health.js.map